import { AppUserValue } from 'lib/actions/apps/app-users'
import { EditActions, RPCAction } from 'lib/actions/rpc'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { GetReturn, ListReturn, Status } from 'lib/rpc'
import { ActionStatus, Collector, expandValue, unaryInvoke, unaryListCollectSinglePage } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    AppEntitlement,
    AppEntitlementSearchServiceSearchRequest,
    AppEntitlementSearchServiceSearchResponse,
    AppEntitlementUserView,
    AppEntitlementView,
    AppEntitlementWithUserBinding,
    GetAppEntitlementRequest,
    GetAppEntitlementResponse,
    UpdateAppEntitlementRequest,
    UpdateAppEntitlementResponse,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementsClient, AppEntitlementSearchServiceClient } from 'pbts/c1/api/app/v1/app_entitlement.client'
import { AppEntitlementUserBinding, GrantEventType } from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { GrantReason } from 'pbts/c1/api/app/v1/app_entitlement_user_binding_reasons'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { AppResource, AppResourceType } from 'pbts/c1/api/app/v1/app_resource'
import { AppUser } from 'pbts/c1/api/app/v1/app_user'
import { ActorObjectPermissions } from 'pbts/c1/api/iam/v1/authorization'
import { Task } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Code as pbCode } from 'pbts/google/rpc/code'
import { Status as pbStatus } from 'pbts/google/rpc/status'
import { Dispatch } from 'react'

export enum AppEntitlementsAction {
    search = 'EntitlementsAction.search',
    get = 'EntitlementsAction.get',
    update = 'EntitlementsAction.update',
}

export type AppEntitlementsActions = GetAction
    | UpdateAction
    | SearchAction

export type SearchAction = {
    type: AppEntitlementsAction.search
    payload: ListReturn<AppEntitlement>
}

export type GetAction = {
    type: AppEntitlementsAction.get
    payload: ActionStatus<AppEntitlement>
}

export type UpdateAction = {
    type: AppEntitlementsAction.update
    payload: GetReturn<AppEntitlement>
}

export interface AppEntitlementUserValue extends AppUserValue {
    appEntitlementUserCreatedAt?: Timestamp
    appEntitlementUserExpiresAt?: Timestamp
}

export interface AppEntitlementWithUserBindingValue {
    id: string
    entitlement?: AppEntitlementValue
    appEntitlementUserBinding?: AppEntitlementUserView
    task?: Task
}

export interface AppEntitlementWithUserBindingValueWithId {
    id: string
    entitlement?: AppEntitlementValue
    appEntitlementUserBinding?: AppEntitlementUserView
    grantSources?: Array<AppEntitlementRef>
}

export type AppEntitlementUserBindingWithReasonsValue = {
    app?: App
    appUser?: AppUserValue
    entitlement?: AppEntitlementValue
    user?: User
    binding?: AppEntitlementUserBinding,
    reasons?: Array<GrantReason>
    appId: string
    appEntitlementId: string
    appUserId: string
    deprovisionAt?: Timestamp
}

export interface AppUserValueWithEntitlementValue {
    app?: App
    appUser?: AppUserValue
    entitlement?: AppEntitlementValue
    user?: User
}

export type AppEntitlementUserBindingHistoryValue = {
    app?: App
    appUser?: AppUserValue
    entitlement?: AppEntitlementValue
    revokedAt?: Timestamp
    grantedAt?: Timestamp
    appId?: string
    appEntitlementId?: string
    appUserId?: string
}

export type AppEntitlementUserBindingFeedValue = {
    app?: App
    appUser?: AppUserValue
    entitlement?: AppEntitlementValue
    ticket?: Task
    date?: Timestamp
    appId?: string
    appEntitlementId?: string
    appUserId?: string
    eventType?: GrantEventType
    ticketId?: string
}

export const expandEntitlementPerAppView = (
    msg: AppEntitlementWithUserBinding | undefined,
    values: Any[],
): AppEntitlementWithUserBindingValue => {
    const expandedEntitlement = expandAppEntitlementView(msg?.entitlement, values)

    return {
        id: expandedEntitlement.id || '',
        entitlement: expandedEntitlement,
        appEntitlementUserBinding: msg?.appEntitlementUserBinding,
    }
}

export const expandAppEntitlementUserView = (
    msg: AppEntitlementUserView | undefined,
    values: Any[],
): AppEntitlementUserValue => {
    const appUserView = msg?.appUser

    let app = expandValue<App>(appUserView?.appPath || '', App, values)
    if (app === undefined) {
        const appErr = expandValue<pbStatus>(appUserView?.appPath || '', pbStatus, values)
        if (appErr?.code === pbCode.NOT_FOUND) {
            app = App.create({
                displayName: 'Deleted',
            })
        }
    }

    if (app?.deletedAt) {
        app.displayName += ' [Deleted]'
    }

    return {
        ...AppUser.create(appUserView?.appUser ?? {}),
        app,
        identityUser: expandValue<User>(appUserView?.identityUserPath || '', User, values),
        appEntitlementUserCreatedAt: msg?.appEntitlementUserBindingCreatedAt,
        appEntitlementUserExpiresAt: msg?.appEntitlementUserBindingDeprovisionAt,
    }
}

export interface AppEntitlementValue extends AppEntitlement {
    app?: App
    appResourceType?: AppResourceType
    appResource?: AppResource
    objectPermissions?: ActorObjectPermissions
}

export interface AppEntitlementValueWithRevokable extends AppEntitlementValue {
    isRevokable: boolean
}

export interface CatalogEntryValue extends AppEntitlement {
    app?: App
    appResourceType?: AppResourceType
    appResource?: AppResource
    enrolledMembersCount?: number
    objectPermissions?: ActorObjectPermissions
}

export interface AppEntitlementValueWithOneOwner {
    entitlement: AppEntitlementValue
    user: User
}

export interface AppEntitlementValueWithDeactivatedOwner {
    entitlement: AppEntitlementValue
    entitlementOwners: User[]
}

export const getAppEntitlement = (
    dispatch: Dispatch<AppEntitlementsActions>,
    client: AppEntitlementsClient | undefined,
    id: string,
    appId: string,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<GetAppEntitlementRequest, GetAppEntitlementResponse>(
        GetAppEntitlementRequest.create({
            id,
            appId,
            expandMask: { paths: ['*'] },
        }),
        client.get.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: AppEntitlementsAction.get,
                    payload: {
                        status: Status.OK,
                        item: expandAppEntitlementView(payload.item.appEntitlementView, payload.item.expanded),
                    },
                })
            } else {
                dispatch({
                    type: AppEntitlementsAction.get,
                    payload,
                })
            }
        },
        notifyClient,
    )
}

export const expandAppEntitlementView = (msg: AppEntitlementView | undefined, values: Any[]): AppEntitlementValue => {
    let app = expandValue<App>(msg?.appPath || '', App, values)
    if (app === undefined) {
        const appErr = expandValue<pbStatus>(msg?.appPath || '', pbStatus, values)
        if (appErr?.code === pbCode.NOT_FOUND) {
            app = App.create({
                displayName: 'Deleted',
            })
        }
    }

    if (app?.deletedAt) {
        app.displayName += ' [Deleted]'
    }

    return {
        ...AppEntitlement.create(msg?.appEntitlement ?? {}),
        app,
        appResourceType: expandValue<AppResourceType>(msg?.appResourceTypePath || '', AppResourceType, values),
        appResource: expandValue<AppResource>(msg?.appResourcePath || '', AppResource, values),
        objectPermissions: msg?.objectPermissions,
    }
}
export class AppEntitlementSearchCollector extends Collector<AppEntitlementValue> {
    push(resp: AppEntitlementSearchServiceSearchResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AppEntitlementValue => {
                return expandAppEntitlementView(v, resp.expanded)
            }),
        )
    }
}
export const searchAppEntitlements = (
    dispatch: Dispatch<SearchAction>,
    client: AppEntitlementSearchServiceClient | undefined,
    request?: AppEntitlementSearchServiceSearchRequest,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client || !request) {
        return emptyCancel
    }
    request.expandMask = { paths: ['*'] }
    request.pageToken = request.pageToken || ''
    const collector = new AppEntitlementSearchCollector()
    return unaryListCollectSinglePage<
        AppEntitlementSearchServiceSearchRequest,
        AppEntitlementSearchServiceSearchResponse
    >(
        request,
        client.search.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppEntitlementsAction.search,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: AppEntitlementsAction.search,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const updateEntitlementField = <T>(
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    client: AppEntitlementsClient | undefined,
    makeRequest: (value: T) => UpdateAppEntitlementRequest | undefined,
): ((value: T) => void) => {
    const doEdit = async (value: T): Promise<void> => {
        if (!client) {
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(value)
        if (req === undefined) {
            return
        }
        req.expandMask = { paths: ['*'] }

        unaryInvoke<UpdateAppEntitlementRequest, UpdateAppEntitlementResponse>(
            req,
            client.update.bind(client),
            (payload) => {
                if (payload.status === Status.OK) {
                    dispatch({
                        type: AppEntitlementsAction.update,
                        payload: {
                            status: Status.OK,
                            item: expandAppEntitlementView(payload.item.appEntitlementView, payload.item.expanded),
                        },
                    })
                    dispatchField({
                        type: RPCAction.edit,
                        payload,
                    })
                } else {
                    dispatch({
                        type: AppEntitlementsAction.update,
                        payload,
                    })
                }
            },
        )
    }

    return (value: T): void => {
        doEdit(value)
    }
}
